const BASE_PATHS = new Set(['/business', '/business/settings', '/business/verification', '/business/page', '/business/support-access', '/business/deliveries', '/business-workspaces']);

const PATH_REQUIREMENTS = new Map([
  ['/business/orders', ['services']],
  ['/business/customers', ['operations']],
  ['/business/reports', ['operations']],
  ['/business/finance', ['operations']],
  ['/business/services', ['services']],
  ['/business/calendar', ['calendar']],
  ['/business/visit-schedule', ['visitScheduling']],
  ['/business/team', ['team']],
  ['/business/shop-orders', ['shop']],
  ['/business/returns', ['shop']],
  ['/business/products', ['shop']],
  ['/business/trade', ['shop']],
  ['/business/promotions', ['shop']],
  ['/business/replenishment', ['shop']],
  ['/business/inventory', ['shop']],
  ['/business/stocktakes', ['shop']],
  ['/business/catalog-imports', ['shop']],
  ['/business/payables', ['shop']],
  ['/business/supplier-credits', ['shop']],
  ['/business/supplier-stock-returns', ['shop']],
  ['/business/supplier-advances', ['shop']],
  ['/business/bank-reconciliation', ['shop']],
  ['/business/warehouses', ['shop']],
  ['/business/staff-access', ['shop', 'team']],
  ['/business/purchasing', ['shop']],
  ['/business/operations', ['workbench']],
  ['/business/map', ['fieldOperations']],
  ['/business/dispatch', ['fieldOperations']],
  ['/business/fleet', ['fieldOperations']],
  ['/business/board', ['workbench']],
  ['/business/zones', ['fieldOperations']],
  ['/business/vehicles', ['fieldOperations']],
  ['/business/wallets', ['fieldOperations']],
  ['/business/scorecards', ['fieldOperations']],
  ['/business/cash-reconciliation', ['cashReconciliation']],
]);

function pathWithoutQuery(path) {
  return path.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/';
}

function requirementForPath(path) {
  const normalized = pathWithoutQuery(path);
  if (BASE_PATHS.has(normalized)) return [];
  const entityDetail = normalized.match(/^\/business\/(shop-orders|customers|orders)\/[1-9]\d*$/);
  if (entityDetail) return PATH_REQUIREMENTS.get('/business/' + entityDetail[1]);
  if (normalized.startsWith('/business/products/')) return ['shop'];
  if (normalized.startsWith('/business/team/')) return ['team'];
  return PATH_REQUIREMENTS.get(normalized) || null;
}

/** Build additive capabilities from the intersection of tenant and provider entitlements. */
export function buildProviderCapabilities({
  providerModules,
  tenantModules,
  moduleConfigs = {},
  hasTeam = false,
  requiresFieldOperations = false,
  shopDeliveryFleet = false,
  allowedWorkerRoles = [],
}) {
  const tenantSet = new Set(tenantModules);
  const activeModules = new Set(
    providerModules.filter((moduleKey) =>
      tenantSet.has(moduleKey)
      && moduleConfigs[moduleKey]?.channels?.providerAdminPortal !== false,
    ),
  );

  const hasShop = activeModules.has('shop');
  const hasBooking = activeModules.has('booking');
  const hasHome = activeModules.has('home_services');
  const hasOnline = activeModules.has('online_services');
  const hasServiceModule = hasBooking || hasHome || hasOnline;
  const hasAnyModule = activeModules.size > 0;
  const deliveryDrivers = shopDeliveryFleet && tenantSet.has('shop') && moduleConfigs.shop?.channels?.providerAdminPortal !== false;

  return {
    activeModules: [...activeModules],
    operations: hasAnyModule,
    services: hasServiceModule,
    calendar: hasBooking || hasOnline,
    visitScheduling: hasHome,
    shop: hasShop,
    // Booking/home businesses must be able to add their first team member.
    team: hasTeam || hasBooking || hasHome || deliveryDrivers,
    deliveryDrivers,
    teamRoles: hasAnyModule || deliveryDrivers ? allowedWorkerRoles.map(String) : [],
    workbench: hasHome || (hasShop && requiresFieldOperations),
    fieldOperations: hasHome || (hasShop && requiresFieldOperations),
    cashReconciliation: hasShop || hasHome,
  };
}

export function isProviderPathAllowed(path, capabilities) {
  const requirements = requirementForPath(path);
  if (requirements === null) return false;
  return requirements.every((capability) => capabilities[capability] === true);
}

export function filterProviderPaths(paths, capabilities) {
  return paths.filter((path) => isProviderPathAllowed(path, capabilities));
}
